import React, { type FC, createContext, useEffect, useContext, useState, useRef } from 'react';
import { type ToDo } from '../../interfaces';
import { useApolloClient, useQuery, type ObservableSubscription } from '@apollo/client';
import { useSnackbar } from 'notistack';
import { TODO_QUERY, TODO_SUBSCRIPTION } from '../../queries';
import { useAuth } from 'components/Auth';

interface ToDosProviderProps { }

interface ToDosContextProps {
    toDos?: ToDo[],
    loading: boolean,
    refetch: () => void
}

const ToDosContext = createContext<ToDosContextProps>({
    toDos: undefined,
    loading: true,
    refetch: () => {}
})

const ToDosProvider: FC<ToDosProviderProps> = (props) => {
    const { effectiveProviderData, authenticated } = useAuth();
    const apolloClient = useApolloClient();

    const { enqueueSnackbar } = useSnackbar();

    const [toDos, setToDos] = useState<ToDo[] | undefined>(undefined);
    let { current: toDosSubscription } = useRef<ObservableSubscription | null>(null);
    
    const toDoQuery = useQuery(TODO_QUERY, {
        fetchPolicy: 'standby',
        errorPolicy: 'all',
        onCompleted: (data) => setToDos(data?.toDos),
        notifyOnNetworkStatusChange: true,
        variables: {
            providerId: effectiveProviderData!.providerId
        }
    });

    useEffect(() => {
        if (!authenticated) {
            toDosSubscription?.unsubscribe();
            setToDos(undefined);
        } else if (effectiveProviderData?.providerId) {
            toDoQuery.refetch({ providerId: effectiveProviderData.providerId });
            toDosSubscription?.unsubscribe();

            const subscription = apolloClient
                .subscribe({
                    query: TODO_SUBSCRIPTION,
                    variables: {
                        providerId: effectiveProviderData.providerId
                    }
                })
                .subscribe((result) => {
                    const todo = result.data?.onToDoChange;
                    if (todo) {
                        todo.visibility && enqueueSnackbar(`New ${todo.step} ToDo`, { variant: 'info' });
                        setToDos([...(toDos ?? []), todo]);
                    }
                });

                toDosSubscription = subscription;
        }
    }, [authenticated, effectiveProviderData?.providerId]);

    useEffect(() => {
        setContextValue({
            ...contextValue,
            toDos: toDos,
            loading: toDoQuery.loading
        });
    }, [toDos, toDoQuery.loading])

    const [contextValue, setContextValue] = useState<ToDosContextProps>({
        toDos: toDos,
        loading: toDoQuery.loading,
        refetch: () => toDoQuery.refetch()
    });

    return <ToDosContext.Provider value={contextValue} {...props} />
}

const useToDos = () => useContext(ToDosContext);

export { ToDosProvider, useToDos };